import { useEffect, useRef, useState } from 'react'

// Module-level promise so concurrent mounts don't inject the script twice.
let ytApiPromise: Promise<void> | null = null

function loadYouTubeApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.YT?.Player) return Promise.resolve()
  if (ytApiPromise) return ytApiPromise
  ytApiPromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = resolve
    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    document.head.appendChild(script)
  })
  return ytApiPromise
}

interface UseYouTubePlayerOptions {
  containerId: string
  videoId: string
  onStateChange?: (state: number) => void
  enabled?: boolean
}

interface UseYouTubePlayerResult {
  playerRef: React.MutableRefObject<YT.Player | null>
  ytReady:   boolean
  ytError:   boolean
  duration:  number
}

export function useYouTubePlayer({
  containerId,
  videoId,
  onStateChange,
  enabled = true,
}: UseYouTubePlayerOptions): UseYouTubePlayerResult {
  const playerRef        = useRef<YT.Player | null>(null)
  const [ytReady, setYtReady]   = useState(false)
  const [ytError, setYtError]   = useState(false)
  const [duration, setDuration] = useState(0)

  // Stable callback ref — updated on every render so the YT event handler always
  // calls the latest version without recreating the Player.
  const onStateChangeRef = useRef(onStateChange)
  useEffect(() => { onStateChangeRef.current = onStateChange }, [onStateChange])

  useEffect(() => {
    if (!enabled || !containerId || !videoId) return

    let destroyed = false

    loadYouTubeApi().then(() => {
      if (destroyed) return

      const origin = typeof window !== 'undefined' ? window.location.origin : ''

      playerRef.current = new window.YT.Player(containerId, {
        videoId,
        playerVars: {
          controls:        0,
          enablejsapi:     1,
          origin,
          rel:             0,
          modestbranding:  1,
          playsinline:     1,
        },
        events: {
          onReady: (e) => {
            if (destroyed) return
            e.target.mute()
            setDuration(e.target.getDuration())
            setYtReady(true)
          },
          onStateChange: (e) => {
            onStateChangeRef.current?.(e.data)
          },
          onError: () => {
            if (!destroyed) setYtError(true)
          },
        },
      })
    }).catch(() => {
      if (!destroyed) setYtError(true)
    })

    return () => {
      destroyed = true
      playerRef.current?.destroy()
      playerRef.current = null
      setYtReady(false)
      setYtError(false)
      setDuration(0)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, videoId, enabled])

  return { playerRef, ytReady, ytError, duration }
}
